import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ExternalLink, Hash, Music2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RowCheckbox } from '@/components/ui/range-select';
import { Tag } from '@/components/ui/tag';
import type { RangeCheckboxProps } from '@/hooks/useRangeSelection';
import { cn } from '@/lib/utils';
import { PLATFORM_LABELS, profileUrl } from '../lib/platform';
import { useDiscoveryPaths } from '../workspace/paths';
import { SoundLink } from '../components/SoundLink';
import {
  BAND_LABELS,
  CATEGORY_PILL,
  CONFIDENCE_LABELS,
  confidenceOf,
  formatRate,
  formatReach,
  whyLine,
  type EvidenceRef,
  type SuggestedSearch,
} from './model';

/**
 * One suggested search: the term, where it came from, why it's worth a run
 * and what a run is expected to return. The evidence is the point — a term on
 * its own is a guess; "14 creators who posted use it" is a reason.
 */
export function SuggestionCard({
  suggestion: s,
  selected,
  checkboxProps,
  folderIdByName,
  busy,
  onApprove,
  onSchedule,
  onReject,
}: {
  suggestion: SuggestedSearch;
  selected: boolean;
  /** Absent when the row can't be acted on (already approved or rejected). */
  checkboxProps: RangeCheckboxProps | null;
  /** Folder names → ids, to link evidence that only names its folder. */
  folderIdByName: ReadonlyMap<string, string>;
  busy: boolean;
  onApprove: () => void;
  onSchedule: () => void;
  onReject: () => void;
}): JSX.Element {
  const sound = s.searchMode === 'sound';
  const confidence = confidenceOf(s);
  const basedOn = s.evidence.basedOn;
  const creators = basedOn.filter((b) => b.kind === 'creator').length;
  const basedOnLabel =
    creators === basedOn.length
      ? `${creators} creator${creators === 1 ? '' : 's'}`
      : creators === 0
        ? `${basedOn.length} folder${basedOn.length === 1 ? '' : 's'}`
        : `${basedOn.length} sources`;

  return (
    <li
      data-suggestion={s.publicId}
      className={cn(
        'panel flex gap-1 rounded-xl transition-colors',
        selected ? 'bg-brand/10 ring-1 ring-brand/40' : 'hover:bg-accent/40',
      )}
    >
      {checkboxProps && (
        <RowCheckbox aria-label={`Select ${s.term}`} hitClassName="m-0 items-start self-stretch pb-4 pl-4 pr-1 pt-[18px]" {...checkboxProps} />
      )}
      <div className={cn('flex min-w-0 flex-1 flex-wrap items-start gap-x-6 gap-y-3 p-4', checkboxProps && 'pl-2')}>
        <div className="min-w-0 flex-1 basis-[22rem] space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            {sound && s.musicId ? (
              // The sound itself: cover, quick listen, and a link to check it's the right one.
              <span className="min-w-0 truncate font-semibold">
                <SoundLink platform={s.platform} id={s.musicId} title={s.term} />
              </span>
            ) : (
              <span className="inline-flex min-w-0 items-center gap-1.5 font-semibold">
                {sound ? (
                  <Music2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                ) : (
                  <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <span className="truncate" title={s.term}>
                  {s.term}
                </span>
              </span>
            )}
            <Tag tone="neutral" title={sound ? 'Finds creators already using this sound' : 'Finds creators posting under this hashtag'}>
              {sound ? 'Sound' : 'Hashtag'}
            </Tag>
            <Tag tone="niche" icon={false} data-category={s.category}>
              {CATEGORY_PILL[s.category]}
              {s.sourceLabel ? ` · ${s.sourceLabel}` : ''}
            </Tag>
            {s.platform !== 'tiktok' && <Tag tone="neutral">{PLATFORM_LABELS[s.platform]}</Tag>}
          </div>

          <p className="text-sm text-foreground/90" data-why>
            {whyLine(s)}
          </p>

          {basedOn.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Based on {basedOnLabel}</summary>
              <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                {basedOn.map((b, i) => (
                  <li key={`${b.kind}:${b.label}:${i}`}>
                    <EvidenceLink evidence={b} folderIdByName={folderIdByName} />
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <dl className="flex shrink-0 flex-wrap gap-x-6 gap-y-2 text-sm">
          {s.expectedYield != null && (
            <Stat label="Expected yield" hint={confidence ? CONFIDENCE_LABELS[confidence] : undefined} data-confidence={confidence ?? undefined}>
              {formatRate(s.expectedYield)} <span className="font-sans text-xs font-normal text-muted-foreground">posts / 100 leads</span>
            </Stat>
          )}
          <Stat label="Reach" hint={s.band ? BAND_LABELS[s.band] ?? s.band : undefined}>
            {s.reach ? formatReach(s.reach) : '—'}
          </Stat>
          {s.score != null && <Stat label="Score">{Number(s.score.toFixed(2))}</Stat>}
        </dl>

        {checkboxProps && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button size="sm" disabled={busy} onClick={onApprove} title="Review the filters, then run once">
              Approve…
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={onSchedule} title="Review the filters, then run now and keep repeating">
              <CalendarClock className="h-3.5 w-3.5" />
              Approve &amp; schedule…
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onReject}>
              Reject
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

function Stat({
  label,
  hint,
  children,
  ...rest
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  'data-confidence'?: string;
}): JSX.Element {
  return (
    <div className="min-w-[5.5rem]" {...rest}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-mono font-semibold tabular-nums">{children}</dd>
      {hint && <dd className="text-xs text-muted-foreground">{hint}</dd>}
    </div>
  );
}

function EvidenceLink({ evidence: e, folderIdByName }: { evidence: EvidenceRef; folderIdByName: ReadonlyMap<string, string> }): JSX.Element {
  const paths = useDiscoveryPaths();
  if (e.kind === 'creator') {
    return (
      <a
        href={profileUrl(e.platform, e.label)}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 hover:underline"
      >
        @{e.label}
        <ExternalLink className="h-3 w-3 text-muted-foreground" aria-hidden />
      </a>
    );
  }
  const folderId = e.folderPublicId ?? folderIdByName.get(e.label) ?? null;
  return folderId ? (
    <Link to={paths.folder(folderId)} className="hover:underline">
      {e.label}
    </Link>
  ) : (
    <span>{e.label}</span>
  );
}
